import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, PlusCircle, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAdminNpcs } from '../hooks/useAdminNpcs';
import { useAdminTasks } from '../hooks/useAdminTasks';
import { useAdminLevels } from '../hooks/useAdminLevels';

interface CoinRewardEntry {
  coinId: string;
  coinName: string;
  quantity: number;
}

interface BadgeRewardEntry {
  badgeId: string;
}

export function AdminCreateTaskPage() {
  const navigate = useNavigate();
  const { npcs, loading: npcsLoading } = useAdminNpcs();
  const { tasks: existingTasks, loading: tasksLoading } = useAdminTasks();
  const { levels, loading: levelsLoading } = useAdminLevels();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState('standard');
  const [category, setCategory] = useState('');
  const [xpReward, setXpReward] = useState(0);
  const [npcId, setNpcId] = useState('');
  const [parentId, setParentId] = useState('');
  const [minLevelId, setMinLevelId] = useState('');

  const [npcTextIntro, setNpcTextIntro] = useState('');
  const [npcTextObjectives, setNpcTextObjectives] = useState('');
  const [npcTextInProgress, setNpcTextInProgress] = useState('');
  const [npcTextSuccess, setNpcTextSuccess] = useState('');

  const [coinRewards, setCoinRewards] = useState<CoinRewardEntry[]>([]);
  const [badgeRewards, setBadgeRewards] = useState<BadgeRewardEntry[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addCoinReward = () => {
    setCoinRewards((prev) => [...prev, { coinId: '', coinName: '', quantity: 1 }]);
  };

  const updateCoinReward = (
    index: number,
    field: keyof CoinRewardEntry,
    value: string | number,
  ) => {
    setCoinRewards((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)),
    );
  };

  const removeCoinReward = (index: number) => {
    setCoinRewards((prev) => prev.filter((_, i) => i !== index));
  };

  const addBadgeReward = () => {
    setBadgeRewards((prev) => [...prev, { badgeId: '' }]);
  };

  const updateBadgeReward = (index: number, badgeId: string) => {
    setBadgeRewards((prev) => prev.map((entry, i) => (i === index ? { badgeId } : entry)));
  };

  const removeBadgeReward = (index: number) => {
    setBadgeRewards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Task name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.createLitLegionTask({
        name: name.trim(),
        description: description.trim(),
        taskType,
        category: category.trim() || undefined,
        xpReward,
        npcId: npcId && npcId !== 'none' ? npcId : undefined,
        parentId: parentId && parentId !== 'none' ? parentId : undefined,
        minLevelId: minLevelId && minLevelId !== 'none' ? minLevelId : undefined,
        npcTextIntro: npcTextIntro.trim() || undefined,
        npcTextObjectives: npcTextObjectives.trim() || undefined,
        npcTextInProgress: npcTextInProgress.trim() || undefined,
        npcTextSuccess: npcTextSuccess.trim() || undefined,
        coinRewards: coinRewards.filter((c) => c.coinId.trim()),
        badgeRewards: badgeRewards.filter((b) => b.badgeId.trim()),
      });
      navigate('/a/arche_lit_legion/admin/tasks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin/tasks"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Task List
        </Link>
        <h1 className="text-2xl font-semibold">Create Task</h1>
        <p className="text-sm text-muted-foreground mt-1">Add a new task for players to complete</p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter task name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the task..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taskType">Task Type</Label>
                <Select value={taskType} onValueChange={setTaskType}>
                  <SelectTrigger id="taskType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="multiple_question">Multiple Question</SelectItem>
                    <SelectItem value="check_inventory">Check Inventory</SelectItem>
                    <SelectItem value="user_input">User Input</SelectItem>
                    <SelectItem value="initial">Initial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g., combat, exploration"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="xpReward">XP Reward</Label>
              <Input
                id="xpReward"
                type="number"
                min={0}
                value={xpReward}
                onChange={(e) => setXpReward(parseInt(e.target.value, 10) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="npcId">NPC</Label>
              <Select value={npcId} onValueChange={setNpcId}>
                <SelectTrigger id="npcId">
                  <SelectValue
                    placeholder={npcsLoading ? 'Loading NPCs...' : 'Select NPC (optional)'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No NPC</SelectItem>
                  {npcs.map((npc) => (
                    <SelectItem key={npc.npcId} value={npc.npcId}>
                      {npc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="parentId">Parent Task</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger id="parentId">
                  <SelectValue
                    placeholder={
                      tasksLoading ? 'Loading tasks...' : 'Select parent task (optional)'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No parent task</SelectItem>
                  {existingTasks.map((t) => (
                    <SelectItem key={t.taskId} value={t.taskId}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="minLevelId">Minimum Level</Label>
              <Select value={minLevelId} onValueChange={setMinLevelId}>
                <SelectTrigger id="minLevelId">
                  <SelectValue
                    placeholder={
                      levelsLoading ? 'Loading levels...' : 'Select minimum level (optional)'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No level requirement</SelectItem>
                  {levels.map((level) => (
                    <SelectItem key={level.levelId} value={level.levelId}>
                      Level {level.levelNumber} - {level.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">NPC Text</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="npcTextIntro">Introduction Text</Label>
              <Textarea
                id="npcTextIntro"
                value={npcTextIntro}
                onChange={(e) => setNpcTextIntro(e.target.value)}
                placeholder="What the NPC says when the player first encounters this task..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="npcTextObjectives">Objectives Text</Label>
              <Textarea
                id="npcTextObjectives"
                value={npcTextObjectives}
                onChange={(e) => setNpcTextObjectives(e.target.value)}
                placeholder="Description of what the player needs to do..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="npcTextInProgress">In Progress Text</Label>
              <Textarea
                id="npcTextInProgress"
                value={npcTextInProgress}
                onChange={(e) => setNpcTextInProgress(e.target.value)}
                placeholder="What the NPC says while the task is in progress..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="npcTextSuccess">Success Text</Label>
              <Textarea
                id="npcTextSuccess"
                value={npcTextSuccess}
                onChange={(e) => setNpcTextSuccess(e.target.value)}
                placeholder="What the NPC says when the task is completed..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Coin Rewards</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addCoinReward}>
                <PlusCircle className="h-4 w-4 mr-1" />
                Add Coin
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {coinRewards.length === 0 ? (
              <p className="text-sm text-muted-foreground">No coin rewards added.</p>
            ) : (
              coinRewards.map((coin, index) => (
                <div key={index} className="flex items-end gap-3">
                  <div className="flex-1 space-y-1">
                    <Label>Coin ID</Label>
                    <Input
                      value={coin.coinId}
                      onChange={(e) => updateCoinReward(index, 'coinId', e.target.value)}
                      placeholder="e.g., coin_gold"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label>Display Name</Label>
                    <Input
                      value={coin.coinName}
                      onChange={(e) => updateCoinReward(index, 'coinName', e.target.value)}
                      placeholder="e.g., Gold"
                    />
                  </div>
                  <div className="w-24 space-y-1">
                    <Label>Qty</Label>
                    <Input
                      type="number"
                      min={1}
                      value={coin.quantity}
                      onChange={(e) =>
                        updateCoinReward(index, 'quantity', parseInt(e.target.value, 10) || 1)
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => removeCoinReward(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Badge Rewards</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addBadgeReward}>
                <PlusCircle className="h-4 w-4 mr-1" />
                Add Badge
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {badgeRewards.length === 0 ? (
              <p className="text-sm text-muted-foreground">No badge rewards added.</p>
            ) : (
              badgeRewards.map((badge, index) => (
                <div key={index} className="flex items-end gap-3">
                  <div className="flex-1 space-y-1">
                    <Label>Badge ID</Label>
                    <Input
                      value={badge.badgeId}
                      onChange={(e) => updateBadgeReward(index, e.target.value)}
                      placeholder="e.g., badge_first_quest"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => removeBadgeReward(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/a/arche_lit_legion/admin/tasks')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Task'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
